import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Paper,
    Stack,
    Typography,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    TablePagination,
    TextField,
} from "@mui/material";
import TableComponent from "components/grid";
import { labelStyle, subTextStyle } from "./commonStyles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { LuSearch } from "react-icons/lu";
import { canDeleteSharedPermissions, getPermissions } from "./dmsPermissions";

const Documents = ({
    files,
    columns,
    setSelectedDocId,
    selectedDocId,
    selectedFolderId,
    handleDownload,
    handleFileRename,
    selectedFolderName,
    onShareClick,
    onManageFilePermission,
    permissionList,
    userEmail,
    onDeleteFile,
    isAdmin,
    search,
    setSearch
}) => {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    useEffect(() => {
        setPage(0);
    }, [selectedFolderId, files.length]);

    const pagedFiles = useMemo(() => {
        const start = page * rowsPerPage;
        return files.slice(start, start + rowsPerPage);
    }, [files, page, rowsPerPage]);

    const handleOpenMenu = (event, row) => {
        event.stopPropagation();
        setMenuAnchor(event.currentTarget);
        setSelectedRow(row);
    };

    const handleClose = () => {
        setMenuAnchor(null);
        setSelectedRow(null)
    };

    // const checkPermission = (fileId) => {
    //     console.log("fileId", fileId);
    //     const findedItem = permissionList?.find(item => item.resourceId === fileId && item.permissionType !== "View")
    //     if (findedItem) {
    //         return true;
    //     }
    //     return false;
    // }

    const activePerms = getPermissions(selectedRow?.fileId, "File", permissionList, userEmail, isAdmin);
    const canDelete = canDeleteSharedPermissions(selectedRow?.fileId, "File", permissionList, userEmail, isAdmin);

    const enhancedColumns = columns.map((col) =>
        col.field === "actions"
            ? {
                ...col,
                renderer: (p) => {
                    const perms = getPermissions(p.fileId, "File", permissionList, userEmail, isAdmin);
                    const canShowMenu = perms.canManagePermission || perms.canDownload
                    return <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {canShowMenu && <IconButton size="small" onClick={(e) => handleOpenMenu(e, p)}>
                            <MoreVertIcon fontSize="small" />
                        </IconButton>}
                    </Box>
                },
            }
            : col
    );

    return (
        <Paper
            sx={{
                width: "50%",
                p: 1.5,
                borderRadius: "3px",
                overflow: "hidden",
                border: "1px solid #E2E8F0",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
            }}
        >
            <Stack
                direction={{ xs: "column", lg: "row" }}
                alignItems={{ xs: "stretch", lg: "center" }}
                justifyContent="space-between"
                spacing={1}
            >
                <Box>
                    <Typography sx={labelStyle}>Documents</Typography>
                    {selectedFolderId && <Typography sx={subTextStyle}>Folder: {selectedFolderName}</Typography>}
                </Box>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                    <TextField
                        size="small"
                        placeholder="Search documents"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        disabled={!selectedFolderId}
                        sx={{
                            width: { xs: "100%", lg: 220 },
                            backgroundColor: "#fff",
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "3px",
                                height: 34,
                            },
                            "& .MuiInputBase-input": {
                                fontSize: "13px",
                            },
                            "& .MuiInputBase-input::placeholder": {
                                fontSize: "13px",
                            },
                        }}
                        InputProps={{
                            startAdornment: <LuSearch style={{ marginRight: "8px", color: "#6B7280" }} />,
                        }}
                        type="search"
                        variant="outlined"
                    />
                    <Typography sx={{ ...subTextStyle, whiteSpace: "nowrap" }}>
                        {files.length} item{files.length === 1 ? "" : "s"}
                    </Typography>
                </Stack>
            </Stack>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ flex: 1, minHeight: 0 }}>
                <TableComponent
                    data={pagedFiles}
                    columns={enhancedColumns}
                    onRowClick={(row) => setSelectedDocId(row.fileId)}
                    selectedRowId={selectedDocId}
                    createSortHandler={() => { }}
                    stickyHeader
                    containerSx={{ height: "100%", overflow: "auto" }}
                />
            </Box>
            <TablePagination
                component="div"
                count={files.length}
                page={page}
                onPageChange={(event, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                    borderTop: "1px solid #eef0f4",
                    minHeight: 44,
                    "& .MuiTablePagination-toolbar": {
                        minHeight: 44,
                        px: 0,
                    },
                }}
            />
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleClose}
            >
                {activePerms?.canDownload && <MenuItem
                    onClick={() => {
                        handleDownload(selectedRow.fileId, selectedRow?.currentVersion, selectedRow?.fileName);
                        handleClose();
                    }}
                >
                    Download
                </MenuItem>}
                {activePerms?.canManagePermission && <MenuItem
                    onClick={() => {
                        handleFileRename(selectedRow);
                        handleClose();
                    }}
                >
                    Rename File
                </MenuItem>}
                {activePerms?.canManagePermission && <MenuItem
                    onClick={() => {
                        onShareClick(selectedRow);
                        handleClose();
                    }}
                >
                    Share File
                </MenuItem>}
                {canDelete && (
                    <MenuItem
                        onClick={() => {
                            onManageFilePermission(selectedRow);
                            handleClose();
                        }}
                    >
                        Manage Permission
                    </MenuItem>
                )}
                {activePerms?.canDeleteFile &&
                    <MenuItem
                        onClick={() => {
                            onDeleteFile(selectedRow)
                            handleClose()
                        }}
                        sx={{ color: "#b42318" }}
                    >
                        Delete File
                    </MenuItem>
                }
            </Menu>
        </Paper>
    );
};

export default Documents;
